import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useBannerOffset } from "@hooks/useBannerOffset";

import { bannerSurface } from "@utils/bannerGradient";

/**
 * Persistent X-able green banner shown after the user confirms which
 * channels to keep in the DowngradeChannelPickerModal. Same pattern as
 * AccountRecoveredGlobalBanner / CancelledGlobalBanner — survives page
 * navigation, dismissible per-confirmation-timestamp, fades out on X.
 *
 * Trigger: PendingDowngradeGlobalModal.handleConfirm writes
 *   localStorage["downgrade-channels-confirmed-at"] = ISO timestamp
 * after the keep-list save succeeds. Banner reads that flag on mount,
 * every route change, and on the `downgrade-channels-confirmed` event.
 *
 * Dismiss key is scoped per-confirmation-timestamp so a future
 * disconnect-channels confirmation re-shows the banner.
 *
 * Reserves its rendered height via --downgrade-success-banner-h on EVERY page
 * (it lives in the fixed SecondHeader container), so content is pushed down
 * below it instead of sliding behind — same as every other global banner.
 */
const FLAG_KEY = "downgrade-channels-confirmed-at";

const DowngradeSuccessBanner: React.FC = () => {
  const { pathname } = useLocation();
  const [flagTimestamp, setFlagTimestamp] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  const readFlag = () => {
    let ts: string | null = null;
    try {
      ts = localStorage.getItem(FLAG_KEY);
    } catch { /* non-fatal */ }
    if (!ts) {
      setFlagTimestamp(null);
      setDismissed(false);
      return;
    }
    setFlagTimestamp(ts);
    try {
      setDismissed(localStorage.getItem(`downgrade-success-banner-dismissed-${ts}`) === "1");
    } catch {
      setDismissed(false);
    }
  };

  useEffect(() => {
    readFlag();
    const handler = () => readFlag();
    window.addEventListener("downgrade-channels-confirmed", handler);
    return () => window.removeEventListener("downgrade-channels-confirmed", handler);
  }, [pathname]);

  const showBanner = !!flagTimestamp && !dismissed;

  useBannerOffset("--downgrade-success-banner-h", showBanner, bannerRef);

  if (!showBanner) return null;

  const handleDismiss = () => {
    if (!flagTimestamp) return;
    try {
      localStorage.setItem(`downgrade-success-banner-dismissed-${flagTimestamp}`, "1");
    } catch { /* non-fatal */ }
    setFadingOut(true);
    window.setTimeout(() => {
      setDismissed(true);
      setFadingOut(false);
    }, 360);
  };

  return (
    <div
      data-banner-surface
      ref={bannerRef}
      data-global-banner
      style={{
        width: "100%",
        marginTop: -1,
        ...bannerSurface("rgb(46, 125, 50)"),
        padding: fadingOut ? "0 24px" : "12px 24px",
        maxHeight: fadingOut ? 0 : 200,
        opacity: fadingOut ? 0 : 1,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        color: "#fff",
        fontFamily: '"Raleway", sans-serif',
        fontSize: "0.875rem",
        position: "relative",
        transition: "opacity 360ms cubic-bezier(0.4, 0, 0.2, 1), max-height 360ms cubic-bezier(0.4, 0, 0.2, 1), padding 360ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <span style={{ fontWeight: 500 }}>
        Channels were successfully updated, and claim processing is continuing.
      </span>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="banner-dismiss"
        style={{
          position: "absolute",
          top: "50%",
          right: 16,
          transform: "translateY(-50%)",
          width: 32,
          height: 32,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.98)",
          cursor: "pointer",
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "color 0.15s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = "#fff"; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.98)"; }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

export default DowngradeSuccessBanner;
